<template>
  <div class="appeals-page-wrapper py-5">
    
    <!-- زر العودة (يختفي في الطباعة) -->
    <div class="container mb-3 no-print">
        <router-link to="/" class="btn btn-outline-secondary btn-sm">
            <i class="fas fa-arrow-right me-1"></i> العودة للرئيسية
        </router-link>
    </div>

    <div class="container">
      <!-- العنوان -->
      <div class="text-center mb-5 no-print">
        <h2 class="fw-bold text-primary-dark">
            <i class="fas fa-balance-scale text-gold me-2"></i> منظومة التظلمات الإلكترونية
        </h2>
        <p class="text-muted">الشهادة الإعدادية - تقديم طلبات الاطلاع</p>
      </div>

      <div class="row justify-content-center">
        <div class="col-lg-8">
          
          <div class="card shadow-lg border-top-gold" id="printableReceipt">
            <div class="card-body p-4">

              <!-- رسائل الأخطاء العامة -->
              <div v-if="error" class="alert alert-danger d-flex align-items-center no-print">
                  <i class="fas fa-exclamation-triangle me-2"></i>
                  {{ error }}
              </div>

              <!-- ============================================= -->
              <!-- الخطوة 1: البحث والتحقق -->
              <!-- ============================================= -->
              <div v-if="step === 1" class="step-animation">
                <h5 class="fw-bold mb-3 border-start border-4 border-primary ps-2">الخطوة 1: بيانات الطالب</h5>
                <form @submit.prevent="verifyStudent">
                  <div class="mb-3">
                    <label class="form-label fw-bold">رقم الجلوس</label>
                    <input v-model="form.seat_number" type="number" class="form-control form-control-lg" placeholder="أدخل رقم الجلوس" required>
                  </div>
                  <div class="mb-3">
                    <label class="form-label fw-bold">الرقم القومي (للمطابقة)</label>
                    <input v-model="form.national_id" type="text" class="form-control" placeholder="أدخل الـ 14 رقم" maxlength="14" required>
                    <div class="form-text text-muted">يجب إدخال الرقم القومي للطالب للتأكد من الهوية قبل عرض الدرجات.</div>
                  </div>
                  <button type="submit" class="btn btn-primary w-100 btn-lg mt-3" :disabled="loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                    <span v-else>تحقق ومتابعة <i class="fas fa-arrow-left ms-1"></i></span>
                  </button>
                </form>
              </div>

              <!-- ============================================= -->
              <!-- الخطوة 2: اختيار المواد -->
              <!-- ============================================= -->
              <div v-if="step === 2" class="step-animation">
                <!-- بيانات الطالب -->
                <div class="alert alert-info border-info d-flex align-items-center mb-4">
                  <div class="icon-box bg-white text-info rounded-circle p-2 me-3 shadow-sm">
                      <i class="fas fa-user-graduate fa-2x"></i>
                  </div>
                  <div>
                    <h5 class="fw-bold mb-1">{{ student.name }}</h5>
                    <small class="d-block text-dark">المدرسة: {{ student.school }}</small>
                    <small class="d-block text-dark">رقم الجلوس: <strong>{{ student.seat_number }}</strong></small>
                  </div>
                </div>

                <h5 class="fw-bold mb-3">الخطوة 2: اختر المواد المراد التظلم فيها</h5>
                <p class="text-muted small bg-light p-2 rounded">
                    <i class="fas fa-info-circle me-1"></i> تكلفة المادة الواحدة: <strong>{{ costPerSubject }} جنيه</strong>
                </p>

                <div class="list-group mb-4 shadow-sm">
                  <label 
                    v-for="sub in subjects" 
                    :key="sub.id" 
                    class="list-group-item list-group-item-action d-flex justify-content-between align-items-center cursor-pointer p-3"
                    :class="{'bg-light-success': form.selected_subjects.includes(sub.id)}"
                  >
                    <div class="d-flex align-items-center">
                      <input class="form-check-input me-3" type="checkbox" :value="{ id: sub.id, name: sub.name, degree: sub.degree }" v-model="form.selected_subjects" style="transform: scale(1.3);">
                      <span class="fs-5">{{ sub.name }}</span>
                    </div>
                    <span class="badge bg-secondary fs-6 rounded-pill">{{ sub.degree }} درجة</span>
                  </label>
                </div>

                <div class="bg-dark text-white p-3 rounded mb-3 text-center shadow-sm">
                  <h5 class="mb-0">الإجمالي المطلوب سداده: <span class="text-gold fw-bold display-6 mx-2">{{ totalCost }}</span> جنيه</h5>
                </div>

                <div class="d-flex gap-2">
                  <button @click="step = 1" class="btn btn-outline-secondary px-4">عودة</button>
                  <button @click="step = 3" class="btn btn-primary flex-grow-1 fw-bold" :disabled="form.selected_subjects.length === 0">
                    متابعة للدفع <i class="fas fa-credit-card ms-2"></i>
                  </button>
                </div>
              </div>

              <!-- ============================================= -->
              <!-- الخطوة 3: الدفع والتأكيد -->
              <!-- ============================================= -->
              <div v-if="step === 3" class="step-animation">
                <h5 class="fw-bold mb-3 border-start border-4 border-primary ps-2">الخطوة 3: تأكيد الطلب</h5>
                
                <div class="alert alert-warning small">
                    يرجى تحويل مبلغ <strong>{{ totalCost }} جنيه</strong> على فوري كود (xxxx) أو حساب بنكي رقم (xxxx)، ثم رفع صورة الإيصال.
                </div>

                <form @submit.prevent="submitAppeal">
                    <div class="mb-3">
                    <label class="form-label fw-bold">رقم الموبايل (للتواصل)</label>
                    <input v-model="form.phone" type="tel" class="form-control form-control-lg" placeholder="01xxxxxxxxx" required pattern="^01[0-2,5]{1}[0-9]{8}$">
                    </div>
                    <div class="mb-3">
                    <label class="form-label fw-bold">رقم ايصال الدفع</label>
                    <input v-model="form.receipt_no" type="text" class="form-control form-control-lg" placeholder="xxxxxxxxx" required >
                    </div>

                    <!-- <div class="mb-4">
                    <label class="form-label fw-bold">صورة إيصال السداد</label>
                    <input type="file" @change="handleFileUpload" class="form-control" accept="image/*" required ref="fileInput">
                    <div class="form-text">يرجى رفع صورة واضحة لإيصال الدفع.</div>
                    </div> -->

                    <div class="d-flex gap-2 mt-4">
                    <button type="button" @click="step = 2" class="btn btn-outline-secondary px-4" :disabled="submitting">عودة</button>
                    <button type="submit" class="btn btn-success flex-grow-1 btn-lg fw-bold" :disabled="submitting">
                        <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
                        <span v-else>تأكيد وحجز الموعد <i class="fas fa-check-circle ms-2"></i></span>
                    </button>
                    </div>
                </form>
              </div>

              <!-- ============================================= -->
              <!-- الخطوة 4: النجاح والطباعة (الإيصال) -->
              <!-- ============================================= -->
            <div v-if="step === 4" class="step-animation d-flex flex-column align-items-center justify-content-center" >
    
    <!-- رسالة النجاح (تظهر على الشاشة فقط) -->
    <div class="text-center mb-4 no-print" >
      <div class="success-icon-container mb-3">
        <i class="fas fa-check"></i>
      </div>
      <h3 class="fw-bold text-success">تم حجز الموعد بنجاح</h3>
      <p class="text-muted">يرجى طباعة الإيصال التالي واصطحابه معك</p>
    </div>

    <!-- منطقة الإيصال (التي سيتم طباعتها) -->
    <div id="printMe" class="receipt-card bg-white" >
      
      <!-- ترويسة الإيصال -->
      <div class="receipt-header">
        <div class="row align-items-center">
          <div class="col-3 text-center">
            <img src="/img/logo/edu.png" alt="Logo" class="receipt-logo grayscale-print">
          </div>
          <div class="col-6 text-center">
            <h5 class="mb-1 fw-bold">مديرية التربية والتعليم</h5>
            <h4 class="mb-1 fw-bold text-dark">لجنة النظام والمراقبة</h4>
            <h6 class="mb-0 border-top border-dark d-inline-block pt-1 mt-1">إيصال حجز تظلم</h6>
          </div>
          <div class="col-3 text-center">
            <div class="qr-placeholder">
              <i class="fas fa-qrcode fa-3x"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- جسم الإيصال -->
      <div class="receipt-body mt-4">
        
        <!-- مربع الموعد المميز -->
        <div class="appointment-banner text-center mb-4">
          <span class="d-block text-muted small mb-1">موعد الحضور المحدد</span>
          <h2 class="display-6 fw-bold mb-0">{{ result.appointment_date }}</h2>
          <span class="d-block fw-bold mt-1">الساعة 9:00 صباحاً</span>
        </div>

        <!-- جدول البيانات -->
        <div class="info-grid">
          <div class="info-item">
            <span class="label">رقم الطلب:</span>
            <span class="value fw-bold">#{{ result.appeal_id }}</span>
          </div>
          <div class="info-item">
            <span class="label">رقم الجلوس:</span>
            <span class="value fw-bold">{{ student.seat_number || '---' }}</span> <!-- تأكد من وجود المتغير -->
          </div>
          <div class="info-item full-width">
            <span class="label">اسم الطالب:</span>
            <span class="value">{{ student.name }}</span>
          </div>
        </div>

        <!-- قسم المواد -->
        <div class="subjects-section mt-3">
          <div class="d-flex justify-content-between align-items-center mb-2 border-bottom pb-1">
            <strong>المواد المتظلم فيها:</strong>
            <span class="badge bg-dark text-white print-badge">{{ form.selected_subjects.length }} مواد</span>
          </div>
          <div class="subjects-list">
            <span 
              v-for="(sub, index) in form.selected_subjects" 
              :key="index" 
              class="subject-tag"
            >
              {{ sub.name }}
            </span>
          </div>
        </div>

        <!-- التنبيهات -->
        <div class="receipt-footer mt-4 pt-2 border-top border-2 border-dark">
          <div class="row">
            <div class="col-8">
              <ul class="instructions-list small mb-0">
                <li>يرجى إحضار صورة ضوئية من بطاقة الرقم القومي لولي الأمر.</li>
                <li>يمنع اصطحاب الهاتف المحمول أثناء الاطلاع.</li>
                <li>الحضور في الموعد المحدد (التأخير يلغي الحجز).</li>
              </ul>
            </div>
            <div class="col-4 text-center d-flex flex-column justify-content-end">
              <span class="small fw-bold mb-3">توقيع المستلم</span>
              <div class="signature-line"></div>
            </div>
          </div>
        </div>

      </div>
      
      <!-- تذييل أسفل الإيصال -->
      <div class="receipt-bottom text-center mt-3">
        <small>تم الاستخراج إلكترونياً بتاريخ {{ new Date().toLocaleDateString('ar-EG') }}</small>
      </div>
    </div>

    <!-- أزرار التحكم -->
    <div class="mt-4 no-print d-flex gap-3">
      <button @click="printPage" class="btn btn-dark btn-lg shadow px-5">
        <i class="fas fa-print me-2"></i> طباعة الإيصال
      </button>
      <button @click="resetForm" class="btn btn-outline-secondary btn-lg">
        عودة للرئيسية
      </button>
    </div>

  </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios';


export default {
  name: 'AppealsRequest',
  data() {
    return {
      step: 1,
      loading: false,
      submitting: false,
      error: null,
      
      costPerSubject: 35, // يجب أن يطابق القيمة في الباك اند للعرض فقط
      
      // البيانات القادمة من السيرفر
      student: null,
      subjects: [],
      result: null,

      // بيانات النموذج
      form: {
        seat_number: '5001',
        national_id: '30910073400164',
        selected_subjects: [],
        phone: '01068294840',
        receipt_file: null
      }
    };
  },
  computed: {
    totalCost() {
      return this.form.selected_subjects.length * this.costPerSubject;
    }
  },
  methods: {
    // 1. التحقق من الطالب وجلب المواد
    async verifyStudent() {
      this.loading = true;
      this.error = null;

      try {
        const response = await api.get(`/appeals/student/${this.form.seat_number}`);
        
        const fetchedStudent = response.data.student;
        
        // التحقق من صحة الرقم القومي (حماية الخصوصية)
        // يجب أن يطابق الرقم القومي المدخل الرقم الموجود في قاعدة البيانات
        if (String(fetchedStudent.national_id) !== String(this.form.national_id)) {
            this.error = "الرقم القومي غير مطابق لرقم الجلوس المدخل.";
            this.loading = false;
            return;
        }

        this.student = fetchedStudent;
        this.subjects = response.data.subjects;
        this.step = 2; // الانتقال للخطوة التالية

      } catch (err) {
        if (err.response && err.response.status === 404) {
            this.error = "رقم الجلوس غير صحيح.";
        } else if (err.response && err.response.status === 422) {
            this.error = err.response.data.message; // رسالة: تم تقديم تظلم مسبقاً
        } else {
            this.error = "حدث خطأ في الاتصال، حاول مرة أخرى.";
        }
      } finally {
        this.loading = false;
      }
    },

    // 2. معالجة رفع الملف
    handleFileUpload(event) {
      const file = event.target.files[0];
      // تحقق بسيط من الحجم والنوع
      if (file && file.size > 2 * 1024 * 1024) { // 2MB
          alert("حجم الصورة كبير جداً، يرجى اختيار صورة أقل من 2 ميجا.");
          this.$refs.fileInput.value = null;
          return;
      }
      this.form.receipt_file = file;
    },

    // 3. إرسال الطلب النهائي
    async submitAppeal() {
      // if (!this.form.receipt_file) {
      //     alert("يرجى رفع صورة الإيصال");
      //     return;
      // }

      this.submitting = true;
      this.error = null;

      // استخدام FormData ضروري عند رفع الملفات
      // let formData = new FormData();
      // formData.append('seat_number', this.form.seat_number);
      // formData.append('national_id', this.form.national_id);
      // formData.append('phone', this.form.phone);
      // formData.append('receipt_file', this.form.receipt_file);
      // // formData.append('selected_subjects',  this.form.selected_subjects);
      
      // // إرسال المصفوفة بشكل صحيح لـ PHP
      // this.form.selected_subjects.forEach((id, index) => {
      //     formData.append(`selected_subjects[${index}]`);
      // });

      try {
        const response = await api.post('/appeals/submit', this.form, {
            // headers: {
            //     'Content-Type': 'multipart/form-data'
            // }
        });

        this.result = response.data; // يحتوي على appointment_date
        this.step = 4; // الانتقال لصفحة النجاح

      } catch (err) {
        console.error(err);
        this.error = err.response?.data?.message || "حدث خطأ أثناء الحفظ";
        // البقاء في نفس الخطوة لرؤية الخطأ
      } finally {
        this.submitting = false;
      }
    },

    // طباعة الإيصال
 printPage() {
        this.$htmlToPaper("printMe");
    },

    // إعادة تعيين النموذج
    resetForm() {
        this.step = 1;
        this.form = {
            seat_number: '',
            national_id: '',
            selected_subjects: [],
            phone: '',
            receipt_file: null
        };
        this.student = null;
        this.subjects = [];
        this.result = null;
    }
  }
};
</script>

<style scoped>
/* التنسيقات العامة */
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;900&display=swap');

.appeals-page-wrapper {
    font-family: 'Cairo', sans-serif;
    background-color: #f8f9fa;
    min-height: 100vh;
}

.text-gold { color: #ffc107; }
.text-primary-dark { color: #002147; }
.border-top-gold { border-top: 5px solid #ffc107 !important; }
.bg-light-success { background-color: #f0fff4; border-color: #198754; }

/* حركة بسيطة بين الخطوات */
.step-animation {
    animation: fadeIn 0.5s ease-in-out;
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}

.cursor-pointer { cursor: pointer; }

/* تنسيقات الطباعة */
@media print {
    body * {
        visibility: hidden;
    }
    .no-print {
        display: none !important;
    }
    
    #printableReceipt, #printableReceipt * {
        visibility: visible;
    }

    #printableReceipt {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        margin: 0;
        padding: 0;
        box-shadow: none !important;
        border: 2px solid #000 !important;
    }
    
    .appointment-box {
        border: 2px solid #000 !important;
        background-color: #fff !important;
    }
    
    /* إجبار المتصفح على طباعة الخلفيات */
    * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }
}

.success-icon-container {
  width: 80px;
  height: 80px;
  background-color: #d1e7dd;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  color: #198754;
  font-size: 40px;
  animation: popIn 0.5s ease-out;
}

/* --- تصميم الإيصال (الكرت) --- */
.receipt-card {
  width: 100%;
  max-width: 600px; /* عرض مناسب يشبه الـ A5 */
  border: 2px solid #333;
  padding: 30px;
  border-radius: 0; /* حواف حادة للرسمية */
  position: relative;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  font-family: 'Cairo', sans-serif; /* تأكد من استخدام خط عربي مناسب */
}

/* خلفية مائية خفيفة للإيصال (اختياري) */
.receipt-card::before {
  content: "";
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000' fill-opacity='0.03'%3E%3Cpath d='M0 0h20L0 20z'/%3E%3C/g%3E%3C/svg%3E");
  z-index: 0;
  pointer-events: none;
}

.receipt-logo {
  height: 70px;
  opacity: 0.9;
}

/* مربع الموعد */
.appointment-banner {
  border: 3px double #333;
  padding: 15px;
  background-color: #f8f9fa;
  margin: 20px 0;
}

/* شبكة المعلومات */
.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 20px;
}

.info-item {
  border-bottom: 1px dashed #ccc;
  padding-bottom: 5px;
  display: flex;
  justify-content: space-between;
}

.info-item.full-width {
  grid-column: span 2;
}

.info-item .label { color: #666; font-size: 0.9rem; }
.info-item .value { color: #000; font-size: 1.1rem; }

/* قائمة المواد */
.subjects-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.subject-tag {
  border: 1px solid #333;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: bold;
  font-size: 0.9rem;
  background: white;
}

.instructions-list li {
  margin-bottom: 4px;
  list-style-type: square;
}

.signature-line {
  border-bottom: 1px solid #000;
  width: 100%;
  height: 30px;
}

@keyframes popIn {
  0% { transform: scale(0); opacity: 0; }
  80% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); }
}

/* --- تنسيقات الطباعة (الأهم) --- */
@media print {
  body * {
    visibility: hidden;
  }
  
  .no-print {
    display: none !important;
  }

  /* إظهار الإيصال فقط */
  #printMe, #printMe * {
    visibility: visible;
  }

  #printMe {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    max-width: 100%; /* استغلال عرض الورقة */
    border: 2px solid #000 !important; /* حدود سوداء واضحة */
    box-shadow: none !important;
    margin: 0;
    padding: 20px;
  }

  /* تحسين الألوان للطباعة */
  .appointment-banner {
    background-color: #eee !important; /* رمادي فاتح للطباعة */
    border-color: #000 !important;
    -webkit-print-color-adjust: exact;
  }

  .print-badge {
    background-color: #000 !important;
    color: #fff !important;
    border: 1px solid #000;
    -webkit-print-color-adjust: exact;
  }

  /* تحويل الصور لدرجات الرمادي لتوفير الحبر */
  .grayscale-print {
    filter: grayscale(100%);
  }
  
  .subject-tag {
      border: 1px solid #000 !important;
      color: #000 !important;
  }
}
</style>